import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Images } from 'utils';
import { v4 as uuidv4 } from 'uuid';
import { Checkbox } from '@mui/material';
import { SelectTypeAutocomplete } from 'components';
import { useFormBuilder } from '../../formContext';
import {
    CELL_TYPES,
    CellTypeIcon,
    cellTypeList,
    getCellMinSelections,
    isCellReadOnly,
    isCellRequired,
    isOptionCellType,
} from '../../tableCell.constants';
import { toMinSelections } from '../../minSelections';
import { GoalModeToggle, TargetModeToggle } from './behaviorSection';
import {
    applyCellTextStyle,
    getCellTextSelectionStyles,
    subscribeCellTextSelection,
    trackCellTextSelection,
} from '../../../utils/cellTextStyle';

// The size box's range (`min` / `max` on the input).
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 72;

// Local-state input that preserves cursor position during typing
export const OptionInput = ({ value, onChange, placeholder }) => {
    const [localValue, setLocalValue] = useState(value || '');
    const inputRef = useRef(null);
    const isTypingRef = useRef(false);

    // Sync from parent only when NOT actively typing
    useEffect(() => {
        if (!isTypingRef.current) {
            setLocalValue(value || '');
        }
    }, [value]);

    const handleChange = useCallback(
        (e) => {
            isTypingRef.current = true;
            const newVal = e.target.value;
            setLocalValue(newVal);
            onChange(newVal);
        },
        [onChange]
    );

    const handleBlur = useCallback(() => {
        isTypingRef.current = false;
    }, []);

    return (
        <input
            ref={inputRef}
            type="text"
            className="table-props-option-input"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
        />
    );
};

/**
 * Minimum selections of a Checkbox group / Multi select column or cell — an
 * optional cell may stay empty, but once anything is picked it needs at least
 * this many (see `minSelections.js`). `onChange` gets the new minimum, 0 for none.
 */
const MinSelectionsInput = ({ value, cellType, optionCount, onChange }) => {
    if (cellType !== CELL_TYPES.CHECKBOX_GROUP && cellType !== CELL_TYPES.MULTI_SELECT) return null;
    const configured = toMinSelections(value);
    return (
        <div className="table-column-card-min-selections">
            <span>Minimum selections</span>
            <input
                type="number"
                min={0}
                max={optionCount || undefined}
                step={1}
                placeholder="None"
                className="table-props-option-input"
                value={configured || ''}
                onChange={(e) => {
                    const raw = e.target.value;
                    const next = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw)));
                    if (Number.isFinite(next)) onChange(next);
                }}
            />
            {configured > optionCount && (
                <span className="table-column-card-min-hint">Only {optionCount} options — all will be needed.</span>
            )}
        </div>
    );
};

const FlagCheckbox = ({ label, checked, onChange, disabled }) => (
    <label className="session-data-column-card-required" style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
        <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            style={{ padding: 0, color: checked ? '#0C66E4' : '#D0D5DD' }}
        />
        <span>{label}</span>
    </label>
);

const RequiredCheckbox = (props) => <FlagCheckbox label="Required" {...props} />;

/**
 * One column of the TABLE: its header name, its type and whether it is
 * required — both defaults for every row of the column — and, for option
 * types, the options every row picks from.
 */
const TableColumnCard = ({ col, isSelected, canRemove, onChange, onRemove }) => {
    const cellType = col.cellType || CELL_TYPES.TEXT;
    const options = col.options || [];
    const setOptions = (next) => onChange({ options: next });

    return (
        <div className={`session-data-column-card ${isSelected ? 'selected' : ''}`}>
            <div className="session-data-column-card-head">
                <CellTypeIcon type={cellType} color="#626F86" />
                <OptionInput value={col.name} onChange={(name) => onChange({ name })} placeholder="Column name" />
                {canRemove && (
                    <button type="button" className="session-data-column-card-remove" title="Remove column" onClick={onRemove}>
                        <img src={Images.closeSingleVector} alt="remove" />
                    </button>
                )}
            </div>

            <div className="session-data-column-card-row">
                <div className="table-column-card-type">
                    <SelectTypeAutocomplete
                        disableClearable={true}
                        name={`tableColumnType-${col.id}`}
                        handleSelect={(event) => onChange({ cellType: event.target.value })}
                        defaultValue={cellType}
                        list={cellTypeList}
                        renderValue={(i) => i?.name}
                        uiType={'tableFilter'}
                        noError={true}
                    />
                </div>
                <RequiredCheckbox checked={!!col.required} onChange={(required) => onChange({ required })} />
            </div>

            <MinSelectionsInput
                value={col.minSelections}
                cellType={cellType}
                optionCount={options.length}
                onChange={(min) => onChange({ minSelections: min || null })}
            />

            {isOptionCellType(cellType) && (
                <div className="table-props-options-list">
                    {options.map((opt, i) => (
                        <div key={opt.id} className="table-props-option-row">
                            <OptionInput
                                value={opt.name}
                                onChange={(value) => setOptions(options.map((o) => (o.id === opt.id ? { ...o, name: value } : o)))}
                                placeholder={`Option ${i + 1}`}
                            />
                            <div className="table-props-option-remove" onClick={() => setOptions(options.filter((o) => o.id !== opt.id))}>
                                <img src={Images.closeSingleVector} alt="remove option" />
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="add-option-btn"
                        onClick={() => setOptions([...options, { id: uuidv4(), name: `Option ${options.length + 1}` }])}
                    >
                        <img src={Images.addBluePlus} alt="add" />
                        <span>Add Option</span>
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * COLUMNS list of the TABLE, the same editor the Session Data Table has.
 * Edits go through the table element's `_action` handshake (like every other
 * edit in this panel) because the element owns the body rows a type change
 * has to reset. The last column cannot be removed: a table with none has
 * nothing to render.
 */
const TableColumnsEditor = ({ tableHead, selectedColId, dispatch }) => (
    <div className="table-props-section">
        <div className="table-props-section-header">COLUMNS</div>
        <div className="properties-info-box blue">
            <img src={Images.infoIcon} alt="info" className="session-data-props-info-icon" />
            <span className="properties-info-text">
                A column's type, Required and Minimum selections apply to every row. Single cells can still override them from CELL TYPES.
            </span>
        </div>

        <div className="session-data-column-cards">
            {tableHead.map((col) => (
                <TableColumnCard
                    key={col.id}
                    col={col}
                    isSelected={col.id === selectedColId}
                    canRemove={tableHead.length > 1}
                    onChange={(patch) => dispatch('updateColumn', { colId: col.id, patch })}
                    onRemove={() => dispatch('removeColumn', col.id)}
                />
            ))}
        </div>

        <div className="session-data-add-column">
            <button type="button" className="session-data-add-column-btn" onClick={() => dispatch('addColumn')}>
                <img src={Images.addBluePlus} alt="add" />
                <span>Add column</span>
            </button>
        </div>
    </div>
);

export const TableColumnsSection = ({ selectedItem, handleUpdate }) => {
    const { isReportBuilder, reportTableCell, selectedGoalId, selectedTargetId } = useFormBuilder();
    const table = selectedItem?.properties?.table || { tableHead: [], tableBody: [] };
    const tableHead = table.tableHead || [];
    // A report keeps the clicked cell in the builder context, not on the table (it would be saved in the report);
    // it counts only while it is a cell of this very table (same field, goal and target).
    const reportCell =
        isReportBuilder &&
        reportTableCell?.attributeId === selectedItem?.id &&
        reportTableCell?.goalId === (selectedGoalId || null) &&
        reportTableCell?.targetId === (selectedTargetId || null) &&
        tableHead.some((c) => c.id === reportTableCell.colId)
            ? reportTableCell
            : null;
    const selectedCellInfo = isReportBuilder ? reportCell : table._selectedCellInfo || null;

    const updateTable = (newTable) => {
        handleUpdate('table', newTable);
    };

    // Text selected in a cell: STYLING shows and changes that text (`cellTextStyle.js`), as the Rich Text toolbar does.
    const [selectionStyles, setSelectionStyles] = useState(getCellTextSelectionStyles);
    useEffect(() => {
        trackCellTextSelection();
        return subscribeCellTextSelection(() => {
            setSelectionStyles(getCellTextSelectionStyles());
            setSizeDraft(null);
        });
    }, []);
    // The size box keeps what is typed until it is a size (typing 17 does not apply 1px on the way).
    const [sizeDraft, setSizeDraft] = useState(null);

    // --- Styling Changes ---
    // Text selected in the cell → styles that text (as in Rich Text); no selection → the whole cell.
    const handleStyleChange = (target, styleKey, value) => {
        if (target === 'cell' && applyCellTextStyle(styleKey, value)) return;
        // `cell`: the cell STYLING shows, styled when the table's own selection was cleared by a click outside it.
        updateTable({ ...table, _action: 'setStyle', _actionPayload: { target, styleKey, value, cell: selectedCellInfo }, _actionId: Date.now() });
    };

    const getCurrentStyles = () => {
        const styles = {
            cellBg: '',
            cellColor: '#172B4D',
            cellBold: false,
            cellItalic: false,
            cellUnderline: false,
            cellSize: '',
        };
        if (!selectedCellInfo) return styles;
        const { rowIndex, colId } = selectedCellInfo;

        const cellStyles = table.cellStyles || {};
        const cellKey = `${rowIndex}_${colId}`;
        const st = cellStyles[cellKey] || {};
        if (st.backgroundColor) styles.cellBg = st.backgroundColor;
        if (st.color) styles.cellColor = st.color;
        if (st.fontWeight === 'bold') styles.cellBold = true;
        if (st.fontStyle === 'italic') styles.cellItalic = true;
        if (st.textDecoration === 'underline') styles.cellUnderline = true;
        if (st.fontSize) styles.cellSize = st.fontSize.replace('px', '');

        return styles;
    };

    const currentStyles = selectionStyles ? { ...getCurrentStyles(), ...selectionStyles } : getCurrentStyles();

    // --- Cell Type Change ---
    const getCurrentCellType = () => {
        if (!selectedCellInfo) return CELL_TYPES.TEXT;
        const { rowIndex, colId, splitSide } = selectedCellInfo;
        const cellTypes = table.cellTypes || {};
        const cellKey = splitSide ? `${rowIndex}_${colId}_${splitSide}` : `${rowIndex}_${colId}`;
        // A split half inherits its cell's type, a cell its column's.
        const col = tableHead.find((c) => c.id === colId);
        return cellTypes[cellKey] || cellTypes[`${rowIndex}_${colId}`] || col?.cellType || CELL_TYPES.TEXT;
    };

    const selectedCol = selectedCellInfo ? tableHead.find((c) => c.id === selectedCellInfo.colId) : null;
    const isSelectedCellRequired = !!selectedCol && isCellRequired(table, selectedCol, selectedCellInfo.rowIndex);
    const handleCellRequiredChange = (required) =>
        updateTable({ ...table, _action: 'setCellRequired', _actionPayload: required, _actionId: Date.now() });
    const isSelectedCellReadOnly = !!selectedCol && isCellReadOnly(table, selectedCol, selectedCellInfo.rowIndex);
    const handleCellReadOnlyChange = (readOnly) =>
        updateTable({ ...table, _action: 'setCellReadOnly', _actionPayload: readOnly, _actionId: Date.now() });
    const selectedCellMinSelections = selectedCol ? getCellMinSelections(table, selectedCol, selectedCellInfo.rowIndex) : null;
    const handleCellMinSelectionsChange = (min) =>
        updateTable({ ...table, _action: 'setCellMinSelections', _actionPayload: min, _actionId: Date.now() });

    const handleCellTypeChange = (newType) => {
        if (!selectedCellInfo) return;
        // Dispatch via _action so the table element handles it with multi-cell support
        updateTable({ ...table, _action: 'changeCellType', _actionPayload: newType, _actionId: Date.now() });
    };

    // --- Options CRUD ---
    const getCurrentOptions = () => {
        if (!selectedCellInfo) return [];
        const { rowIndex, colId, splitSide } = selectedCellInfo;
        const cellOptions = table.cellOptions || {};
        const cellKey = splitSide ? `${rowIndex}_${colId}_${splitSide}` : `${rowIndex}_${colId}`;
        const col = tableHead.find((c) => c.id === colId);
        return cellOptions[cellKey] || col?.options || [];
    };

    const handleAddOption = () => {
        // Dispatch via _action so table element adds to all selected cells
        updateTable({ ...table, _action: 'addOption', _actionId: Date.now() });
    };

    const handleRemoveOption = (optIndex) => {
        updateTable({ ...table, _action: 'removeOption', _actionPayload: optIndex, _actionId: Date.now() });
    };

    const handleOptionNameChange = (optIndex, value) => {
        updateTable({ ...table, _action: 'renameOption', _actionPayload: { optIndex, value }, _actionId: Date.now() });
    };

    const currentCellType = getCurrentCellType();
    const currentOptions = getCurrentOptions();
    // A header cell has no type of its own — its column is set up in COLUMNS.
    const isBodyCellSelected = !!selectedCellInfo && selectedCellInfo.rowIndex >= 0;
    const showOptions = isBodyCellSelected && isOptionCellType(currentCellType);
    const optionsLabels = {
        [CELL_TYPES.RADIO]: 'RADIO OPTIONS',
        [CELL_TYPES.CHECKBOX_GROUP]: 'CHECKBOX GROUP OPTIONS',
        [CELL_TYPES.SELECT]: 'SELECT OPTIONS',
        [CELL_TYPES.MULTI_SELECT]: 'MULTI SELECT OPTIONS',
    };
    const optionsLabel = optionsLabels[currentCellType] || 'OPTIONS';

    return (
        <div className="table-properties-panel">
            {/* Styling */}
            {/* STYLING is offered in a progress report too; the table's structure and cell setup stay template-only. */}
            {selectedCellInfo && (
                <div className="table-props-section">
                    <div className="table-props-section-header">STYLING</div>
                    <div className="behavior-row">
                        <div className="full-width">
                            <div className="flex-align-center full-width" style={{ gap: 16 }}>
                                <div className="color-picker-wrapper">
                                    <span className="option-label" style={{ fontSize: '12px' }}>
                                        Background
                                    </span>
                                    <div className="color-input-container">
                                        <input
                                            type="color"
                                            value={currentStyles.cellBg || '#ffffff'}
                                            onChange={(e) => handleStyleChange('cell', 'backgroundColor', e.target.value)}
                                        />
                                        <span>{currentStyles?.cellBg || '#ffffff'}</span>
                                    </div>
                                </div>
                                <div className="color-picker-wrapper">
                                    <span className="option-label" style={{ fontSize: '12px' }}>
                                        Text Color
                                    </span>
                                    <div className="color-input-container">
                                        <input
                                            type="color"
                                            value={currentStyles.cellColor || '#222420'}
                                            onChange={(e) => handleStyleChange('cell', 'color', e.target.value)}
                                        />
                                        <span>{currentStyles?.cellColor || '#ffffff'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-align-center" style={{ marginTop: 12, gap: 12 }}>
                                <button
                                    className={`table-props-action-btn ${currentStyles.cellBold ? 'active' : ''} flex-justify-align-center`}
                                    style={{ minWidth: 32, width: 32, height: 32 }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleStyleChange('cell', 'fontWeight', currentStyles.cellBold ? 'normal' : 'bold')}
                                >
                                    <b>B</b>
                                </button>
                                <button
                                    className={`table-props-action-btn ${
                                        currentStyles.cellItalic ? 'active' : ''
                                    } flex-justify-align-center`}
                                    style={{ minWidth: 32, width: 32, height: 32 }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleStyleChange('cell', 'fontStyle', currentStyles.cellItalic ? 'normal' : 'italic')}
                                >
                                    <i>I</i>
                                </button>
                                <button
                                    className={`table-props-action-btn ${
                                        currentStyles.cellUnderline ? 'active' : ''
                                    } flex-justify-align-center`}
                                    style={{ minWidth: 32, width: 32, height: 32 }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() =>
                                        handleStyleChange('cell', 'textDecoration', currentStyles.cellUnderline ? 'none' : 'underline')
                                    }
                                >
                                    <u>U</u>
                                </button>
                                <input
                                    title="Font Size"
                                    type="number"
                                    min={MIN_FONT_SIZE}
                                    max={MAX_FONT_SIZE}
                                    placeholder="14"
                                    value={sizeDraft ?? (currentStyles.cellSize || '')}
                                    onChange={(e) => {
                                        const size = e.target.value;
                                        setSizeDraft(size);
                                        if (Number(size) >= MIN_FONT_SIZE && Number(size) <= MAX_FONT_SIZE) handleStyleChange('cell', 'fontSize', size + 'px');
                                    }}
                                    // An emptied box removes the size only once left empty, not while the number is retyped
                                    // (on a whole cell that also clears its text's own sizes).
                                    onBlur={() => {
                                        if (sizeDraft === '' && currentStyles.cellSize) handleStyleChange('cell', 'fontSize', '');
                                        setSizeDraft(null);
                                    }}
                                    style={{
                                        width: 44,
                                        height: 32,
                                        padding: '0 4px',
                                        border: '1px solid #ddd',
                                        borderRadius: 8,
                                        fontFamily: 'sans-serif',
                                        fontSize: 13,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cell Types */}
            {!isReportBuilder && isBodyCellSelected && (
                <div className="table-props-section">
                    <div className="table-props-section-header">CELL TYPES</div>
                    <div className="table-props-cell-types">
                        {cellTypeList.map((ct) => {
                            const isActive = currentCellType === ct.id;
                            return (
                                <button
                                    key={ct.id}
                                    className={`table-props-cell-type-btn ${isActive ? 'active' : ''}`}
                                    onClick={() => handleCellTypeChange(ct.id)}
                                >
                                    <CellTypeIcon type={ct.id} color={isActive ? '#0C66E4' : '#344054'} />
                                    <span>{ct.name}</span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="table-props-cell-required">
                        <div className="flex-align-center" style={{ gap: 16, flexWrap: 'wrap' }}>
                            <RequiredCheckbox
                                checked={isSelectedCellRequired}
                                onChange={handleCellRequiredChange}
                                disabled={isSelectedCellReadOnly}
                            />
                            {/* Read Only: a label staff see but cannot change while filling — never required. */}
                            <FlagCheckbox label="Read Only" checked={isSelectedCellReadOnly} onChange={handleCellReadOnlyChange} />
                        </div>
                        {!isSelectedCellReadOnly && (
                            <MinSelectionsInput
                                value={selectedCellMinSelections}
                                cellType={currentCellType}
                                optionCount={currentOptions.length}
                                onChange={handleCellMinSelectionsChange}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Options Section (for Radio / Checkbox Group) */}
            {showOptions && (
                <div className="table-props-section">
                    <div className="table-props-section-header">{optionsLabel}</div>
                    <div className="table-props-options-list">
                        {currentOptions.map((opt, i) => (
                            <div key={opt.id || i} className="table-props-option-row">
                                {currentCellType === CELL_TYPES.RADIO ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D0D5DD" strokeWidth="2">
                                        <circle cx="12" cy="12" r="9" />
                                    </svg>
                                ) : currentCellType === CELL_TYPES.SELECT || currentCellType === CELL_TYPES.MULTI_SELECT ? (
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#D0D5DD"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    >
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                ) : (
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#D0D5DD"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect x="3" y="3" width="18" height="18" rx="3" />
                                    </svg>
                                )}
                                <OptionInput
                                    value={opt.name || ''}
                                    onChange={(val) => handleOptionNameChange(i, val)}
                                    placeholder={`Option ${i + 1}`}
                                />
                                <div className="table-props-option-remove" onClick={() => handleRemoveOption(i)}>
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path
                                            d="M9.70859 1.70859C10.0992 1.31797 10.0992 0.683594 9.70859 0.292969C9.31797 -0.0976562 8.68359 -0.0976562 8.29297 0.292969L5.00234 3.58672L1.70859 0.296094C1.31797 -0.0945312 0.683594 -0.0945312 0.292969 0.296094C-0.0976562 0.686719 -0.0976562 1.32109 0.292969 1.71172L3.58672 5.00234L0.296094 8.29609C-0.0945312 8.68672 -0.0945312 9.32109 0.296094 9.71172C0.686719 10.1023 1.32109 10.1023 1.71172 9.71172L5.00234 6.41797L8.29609 9.70859C8.68672 10.0992 9.32109 10.0992 9.71172 9.70859C10.1023 9.31797 10.1023 8.68359 9.71172 8.29297L6.41797 5.00234L9.70859 1.70859Z"
                                            fill="#667085"
                                        />
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="add-option-btn" onClick={handleAddOption} style={{ marginTop: 4 }}>
                        <img src={Images.addBluePlus} alt="add" />
                        <span>Add Option</span>
                    </button>
                </div>
            )}

            {!isReportBuilder && (
                <TableColumnsEditor
                    tableHead={tableHead}
                    selectedColId={selectedCellInfo?.colId}
                    dispatch={(action, payload) =>
                        updateTable({ ...table, _action: action, _actionPayload: payload, _actionId: Date.now() })
                    }
                />
            )}

            {/* Display Options */}
            {!isReportBuilder && (
                <div className="table-props-section">
                    <div className="table-props-section-header">DISPLAY OPTIONS</div>
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Header</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={table.showHeader !== false}
                                onChange={(e) => updateTable({ ...table, showHeader: e.target.checked })}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Borders</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={table.showBorders !== false}
                                onChange={(e) => updateTable({ ...table, showBorders: e.target.checked })}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                    <GoalModeToggle selectedItem={selectedItem} handleUpdate={handleUpdate} />
                    <TargetModeToggle selectedItem={selectedItem} handleUpdate={handleUpdate} />
                </div>
            )}

            {/* Quick Guide */}
            {!isReportBuilder && (
                <div className="table-props-section">
                    <div className="table-props-section-header">QUICK GUIDE</div>
                    <div className="table-props-guide">
                        <div className="table-props-guide-item">
                            <span className="guide-title">Multi-Select</span>
                            <span className="guide-desc">
                                Hold <kbd>Shift</kbd> or <kbd>Ctrl</kbd> while clicking cells
                            </span>
                        </div>
                        <div className="table-props-guide-item">
                            <span className="guide-title">Edit Content</span>
                            <span className="guide-desc">Double-click any cell to edit inline</span>
                        </div>
                        <div className="table-props-guide-item">
                            <span className="guide-title">Right-Click Menu</span>
                            <span className="guide-desc">Access all actions with right-click</span>
                        </div>
                        <div className="table-props-guide-item">
                            <span className="guide-title">Bulk Operations</span>
                            <span className="guide-desc">Select multiple cells to change types at once</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
